import { createFileRoute } from "@tanstack/react-router"

import { useCallback, useState } from "react"
import { supabase } from "@fieldbook/common/supabase"
import { type SubmitHandler, useForm } from "react-hook-form"
import { Spinner } from "@fieldbook/ui/spinner"
import { FormField } from "@fieldbook/ui/formField"
import { Button } from "@fieldbook/ui/button"
import { ButtonLink } from "@fieldbook/ui/button-link"

type FormData = {
  email: string
}

const ResetPasswordRequestPage = () => {
  const [success, setSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isValid, isSubmitting },
    watch,
  } = useForm<FormData>({ mode: "all" })

  const emailValue = watch("email")

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async ({ email }) => {
      clearErrors()
      const { data, error } = await supabase.auth.resetPasswordForEmail(email)

      if (error) {
        setError("email", { message: error.message })
        return
      } else if (data) setSuccess(true)
    },
    [clearErrors, setError, setSuccess],
  )

  return (
    <div className="flex h-full flex-col items-center justify-center">
      <div className="pwa:text-lg min-h-1/3 w-full max-w-md p-4 md:rounded-lg">
        <div>
          <h4 className="mb-2 text-xl font-bold">Reset Password</h4>
          {!success && (
            <p>Enter your email address to request a password reset.</p>
          )}
        </div>
        {!success && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="mt-2 flex flex-col gap-2"
          >
            <FormField
              label="Email"
              type="email"
              {...register("email", {
                required: "Required",
              })}
              error={errors.email}
            />

            {!errors.root?.login && <div className="h-[34px]" />}
            <Button disabled={!isValid || isSubmitting} type="submit">
              {!isSubmitting && "Submit"}
              {isSubmitting && <Spinner />}
            </Button>
          </form>
        )}
        {success && (
          <div className="flex flex-col gap-2">
            <p>
              Password reset request email has been sent to {emailValue}. Please
              check your inbox for the email.
            </p>
            <ButtonLink to="/auth/login" className="w-full">
              Back to Login
            </ButtonLink>
          </div>
        )}
      </div>
    </div>
  )
}

export const Route = createFileRoute("/auth/reset-password-request")({
  component: ResetPasswordRequestPage,
})
